// Users browse state: filters + opaque-cursor pagination.
//
// The API cursor is forward-only & opaque (FACTS PACK §3a). To offer prev/next we
// keep a client stack of the cursors that opened each page: pageCursors[i] is the
// cursor that produced page i (pageCursors[0] === undefined = first page). "Next"
// pushes the server's nextCursor; "Prev" pops. nextCursor is already normalized to
// null-when-done by listUsers (normalizeCursor).
//
// Email search is a DIFFERENT axis (LOOKUP, flat object) — handled in the page, not
// here. This hook owns the BROWSE list only.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { listUsers } from './usersApi';
import type { AdminUser } from './usersApi';
import { identityMatch, identityRoles, userBrowseStatusMatch } from './userModel';
import type { IdentityClass } from './userModel';

const PAGE_SIZE = 25;
// Bound the empty-chunk walk (DDB filters after Limit): 8 chunks × 25 scanned rows
// per fetch keeps worst-case latency sane; a partial page just ends the walk early.
const MAX_SCAN_CHUNKS = 8;

export interface BrowseFilters {
  // region intentionally absent (spec §3.2 S2, D81③): browse mode ignores
  // region server-side, so surfacing it was a no-op filter.
  status: string;
  /** 08 §2.1 auth-pool family (family_id) filter; '' = all. Not surfaced in the UI. */
  family: string;
  /** eco-* ecosystem EFFECTIVE-membership filter (原 OR 加入; common#24, owner
   *  2026-07-12). '' = all ecosystems. Server keeps rows whose effective set
   *  includes this eco id. */
  ecosystem: string;
  /** Identity tab translated to the required `role=` set (02 §3.3.1). */
  identity: IdentityClass;
}

export interface UserBrowseState {
  users: AdminUser[] | null;
  loading: boolean;
  error: unknown;
  pageIndex: number; // 0-based
  hasPrev: boolean;
  hasNext: boolean;
  reload: () => void;
  next: () => void;
  prev: () => void;
}

export function useUserBrowse(filters: BrowseFilters): UserBrowseState {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // cursor that opened each page; index 0 is undefined (first page)
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const alive = useRef(true);
  const reqSeq = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | undefined) => {
      const seq = ++reqSeq.current;
      setLoading(true);
      setError(null);
      try {
        // Always role-scoped: /users owns end-user classes only; maintainer
        // accounts live on /team (S9) and never appear here.
        const roles = identityRoles(filters.identity);
        // DDB Scan applies Limit BEFORE the role/status filter, so a scan chunk can
        // come back empty (or sparse) while next_cursor still advances. Accumulate
        // chunks until the page is full or the cursor is exhausted (bounded), so the
        // UI never strands the operator on an empty page that still has a "next".
        const rows: AdminUser[] = [];
        let walk: string | undefined = cursor;
        let more: string | null = null;
        for (let i = 0; i < MAX_SCAN_CHUNKS; i++) {
          const page = await listUsers({
            status: filters.status || undefined,
            roles: roles.length > 0 ? roles : undefined,
            family: filters.family || undefined,
            ecosystem: filters.ecosystem || undefined,
            limit: PAGE_SIZE,
            cursor: walk || undefined,
          });
          // stale walk (filters changed / unmount) must stop burning requests
          if (!alive.current || seq !== reqSeq.current) return;
          // Belt-and-braces: keep both role and internal-family identity axes
          // correct against a backend build that predates either filter.
          rows.push(...page.users.filter((u) => identityMatch(filters.identity, u.role, u.family_id) && userBrowseStatusMatch(filters.status, u.status)));
          more = page.nextCursor;
          if (rows.length >= PAGE_SIZE || !page.nextCursor) break;
          walk = page.nextCursor;
        }
        if (!alive.current || seq !== reqSeq.current) return;
        setUsers(rows);
        setNextCursor(more);
      } catch (e) {
        if (!alive.current || seq !== reqSeq.current) return;
        setUsers(null);
        setNextCursor(null);
        setError(e instanceof ApiError ? e : e instanceof Error ? e : new Error('載入失敗'));
      } finally {
        if (alive.current && seq === reqSeq.current) setLoading(false);
      }
    },
    [filters.status, filters.family, filters.ecosystem, filters.identity]
  );

  // Reset to page 0 whenever filters change.
  useEffect(() => {
    alive.current = true;
    setPageCursors([undefined]);
    void fetchPage(undefined);
    return () => {
      alive.current = false;
    };
  }, [fetchPage]);

  const reload = useCallback(() => {
    const current = pageCursors[pageCursors.length - 1];
    void fetchPage(current);
  }, [fetchPage, pageCursors]);

  const next = useCallback(() => {
    if (!nextCursor) return;
    setPageCursors((s) => [...s, nextCursor]);
    void fetchPage(nextCursor);
  }, [fetchPage, nextCursor]);

  const prev = useCallback(() => {
    setPageCursors((s) => {
      if (s.length <= 1) return s;
      const reduced = s.slice(0, -1);
      void fetchPage(reduced[reduced.length - 1]);
      return reduced;
    });
  }, [fetchPage]);

  return {
    users,
    loading,
    error,
    pageIndex: pageCursors.length - 1,
    hasPrev: pageCursors.length > 1,
    hasNext: nextCursor !== null,
    reload,
    next,
    prev,
  };
}
